import { Box, Typography, Button, Grid, Chip } from '@mui/material'
import { styled, keyframes } from '@mui/material/styles'
import { Shield, TrendingUp, Visibility } from '@mui/icons-material'
import { useAccount } from 'wagmi'
import { COLORS } from '../constants/colors'
import { TEXT } from '../constants/text'
import LiveTicker from '../components/signals/LiveTicker'

const goldGlow = keyframes`
  0%, 100% { text-shadow: 0 0 4px ${COLORS.gold.primary}, 0 0 12px ${COLORS.gold.primary}; }
  50%      { text-shadow: 0 0 8px ${COLORS.gold.primary}, 0 0 25px ${COLORS.gold.light}; }
`

const HeroSection = styled(Box)(() => ({
  textAlign: 'center',
  padding: '60px 20px 40px',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: `radial-gradient(ellipse at 50% 0%, ${COLORS.gold.muted} 0%, transparent 60%)`,
    pointerEvents: 'none',
  },
}))

const FeatureCard = styled(Box)(() => ({
  padding: '24px 20px',
  borderRadius: 14,
  border: `1px solid ${COLORS.border.light}`,
  background: COLORS.bg.glass,
  textAlign: 'center',
  transition: 'all .3s',
  '&:hover': {
    background: COLORS.bg.glassHover,
    borderColor: COLORS.gold.border,
    transform: 'translateY(-4px)',
  },
}))

const features = [
  {
    icon: <Shield sx={{ fontSize: 32, color: COLORS.green.primary }} />,
    title: 'Rug Detection',
    desc: 'On-chain rug scoring in under 1.5 seconds. Mint/freeze authority, LP lock, deployer history, Jito bundle detection.',
  },
  {
    icon: <Visibility sx={{ fontSize: 32, color: COLORS.blue.primary }} />,
    title: 'Smart Money Tracking',
    desc: 'Track Top 100 wallets in real-time. See whale entries before the crowd. Smart money convergence signals.',
  },
  {
    icon: <TrendingUp sx={{ fontSize: 32, color: COLORS.gold.primary }} />,
    title: 'Boss Man Signals',
    desc: 'KOL wallet overlays, elevated conviction signals, Telegram + webhook alerts. Elite intelligence layer.',
  },
]

export default function LandingPage({ onNavigate, tickerStats }) {
  const { isConnected } = useAccount()

  return (
    <Box>
      <HeroSection>
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 900,
              fontSize: { xs: '2rem', md: '3rem' },
              color: COLORS.gold.primary,
              animation: `${goldGlow} 3s ease-in-out infinite`,
              mb: 2,
            }}
          >
            {TEXT.HERO.HEADLINE}
          </Typography>

          <Typography
            sx={{
              fontSize: { xs: '0.9rem', md: '1.1rem' },
              color: COLORS.text.secondary,
              maxWidth: 600,
              mx: 'auto',
              mb: 4,
              lineHeight: 1.7,
            }}
          >
            {TEXT.HERO.SUB}
          </Typography>

          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            {!isConnected && (
              <Button
                variant="contained"
                size="large"
                sx={{
                  background: `linear-gradient(135deg, ${COLORS.gold.primary}, ${COLORS.gold.dark})`,
                  color: '#000',
                  fontWeight: 700,
                  px: 4,
                  borderRadius: 2,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${COLORS.gold.light}, ${COLORS.gold.primary})`,
                  },
                }}
              >
                {TEXT.HERO.CTA_PRIMARY}
              </Button>
            )}
            <Button
              variant="outlined"
              size="large"
              onClick={() => onNavigate('feed')}
              sx={{
                borderColor: COLORS.border.medium,
                color: COLORS.text.primary,
                fontWeight: 600,
                px: 4,
                borderRadius: 2,
                '&:hover': {
                  borderColor: COLORS.gold.primary,
                  color: COLORS.gold.primary,
                },
              }}
            >
              {TEXT.HERO.CTA_SECONDARY}
            </Button>
          </Box>
        </Box>
      </HeroSection>

      {/* Live ticker preview */}
      <Box sx={{ mb: 5 }}>
        <LiveTicker stats={tickerStats} />
      </Box>

      {/* Feature cards */}
      <Grid container spacing={3} sx={{ mb: 5 }}>
        {features.map((f, i) => (
          <Grid item xs={12} md={4} key={i}>
            <FeatureCard>
              <Box sx={{ mb: 1.5 }}>{f.icon}</Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1rem', mb: 1 }}>
                {f.title}
              </Typography>
              <Typography sx={{ fontSize: '0.8rem', color: COLORS.text.secondary, lineHeight: 1.6 }}>
                {f.desc}
              </Typography>
            </FeatureCard>
          </Grid>
        ))}
      </Grid>

      {/* Trust bar */}
      <Box sx={{ textAlign: 'center', py: 4, borderTop: `1px solid ${COLORS.border.subtle}` }}>
        <Typography sx={{ fontSize: '0.7rem', color: COLORS.text.muted, letterSpacing: '2px', textTransform: 'uppercase' }}>
          {TEXT.FOOTER.NON_CUSTODIAL}
        </Typography>
      </Box>
    </Box>
  )
}
